import { useState } from 'react';
import { Avatar, Card, Chip } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { cn } from '@/lib/utils';
import { formatRelative } from '@/lib/format';
import type { User } from '@/lib/auth';
import { AVATAR_COLORS } from '../schemas';
import { useUpdateProfile } from '../api';

interface Props {
  user: User;
}

export function AvatarSection({ user }: Props) {
  const update = useUpdateProfile(user.id);
  const [selected, setSelected] = useState(user.avatar_color ?? AVATAR_COLORS[0]);

  function pickColor(color: (typeof AVATAR_COLORS)[number]) {
    setSelected(color);
    update.mutate({
      name: user.name,
      cargo: user.cargo ?? '',
      avatar_color: color,
    });
  }

  return (
    <Card padding="lg">
      <Stack gap="md">
        <div className="flex items-center gap-4">
          <Avatar name={user.name} color={selected} size="xl" />
          <div className="flex flex-col gap-1 min-w-0">
            <span className="text-base font-semibold text-text">{user.name}</span>
            {user.cargo && <span className="text-xs text-text-2">{user.cargo}</span>}
            <div className="flex items-center gap-2 mt-1">
              <Chip tone="accent" size="sm">
                {user.role}
              </Chip>
              {user.last_login && (
                <span className="text-[10px] text-text-muted">
                  Último acesso {formatRelative(user.last_login)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-text-muted">
            Cor do avatar
          </span>
          <div className="flex flex-wrap gap-2">
            {AVATAR_COLORS.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => pickColor(c)}
                aria-label={`Selecionar cor ${c}`}
                aria-pressed={c === selected}
                className={cn(
                  'size-6 rounded-full border-2 transition-transform',
                  c === selected ? 'border-text scale-110' : 'border-transparent hover:scale-105',
                )}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>
      </Stack>
    </Card>
  );
}
